/**
 * Wat er in één keer door de transcriptie past.
 *
 * De transcriptie-API accepteert 25 MB, maar dat is in de praktijk een grens op
 * speelduur en niet op bestandsgrootte: alles gaat eerst door de normalisatie
 * naar Opus op 24 kbps (`container/server.js`). Een video van een gigabyte die
 * veertig minuten duurt komt daar als 7 MB uit; een audiobestand van 300 MB dat
 * vier uur duurt niet.
 *
 * Daarom staat de grens hier in seconden en niet in megabytes. De browser
 * rekent ermee voordat er iets wordt geüpload, de Worker gebruikt dezelfde
 * bytes-grens na de omzetting. Zonder die gedeelde herkomst lopen de twee uit
 * elkaar en weigert het ene wat het andere toelaat.
 *
 * Gemeten komt er 20,4 kbps uit (Metingen §5), dus rekenen met de ingestelde 24
 * houdt marge: een opname die hier net niet langs komt, past in werkelijkheid
 * meestal nog wel.
 */
export const MAX_TRANSCRIPTIE_BYTES = 25 * 1024 * 1024;
const OPUS_BITS_PER_SECONDE = 24_000;
export const MAX_OPNAME_SECONDEN = Math.floor((MAX_TRANSCRIPTIE_BYTES * 8) / OPUS_BITS_PER_SECONDE);
/** Hele minuten. Vergelijken en tonen gebeurt allebei hierop, zodat een opname
 *  nooit wordt geweigerd met een duur die gelijk lijkt aan de grens. */
const minuten = (seconden) => Math.floor(seconden / 60);
/**
 * `false` bij een onbekende duur, en dat is opzet: een browser die het formaat
 * niet kan uitlezen is geen reden om een geldige opname tegen te houden. Dan
 * merkt de Worker het na de omzetting alsnog.
 */
export function opnameTeLang(seconden) {
    if (seconden == null || !Number.isFinite(seconden) || seconden <= 0)
        return false;
    return minuten(seconden) > minuten(MAX_OPNAME_SECONDEN);
}
/** "2 uur 25 min", "28 min" — naast de bestandsgrootte. */
export function duurKort(seconden) {
    const m = minuten(seconden);
    if (m < 1)
        return 'minder dan een minuut';
    const uur = Math.floor(m / 60);
    const rest = m % 60;
    if (uur === 0)
        return `${rest} min`;
    return rest === 0 ? `${uur} uur` : `${uur} uur ${rest} min`;
}
/** "2 uur en 25 minuten", "28 minuten" — in een lopende zin. */
export function duurInWoorden(seconden) {
    const m = minuten(seconden);
    if (m < 1)
        return 'minder dan een minuut';
    const uur = Math.floor(m / 60);
    const rest = m % 60;
    const uurTekst = uur === 1 ? '1 uur' : `${uur} uur`;
    const minTekst = rest === 1 ? '1 minuut' : `${rest} minuten`;
    if (uur === 0)
        return minTekst;
    return rest === 0 ? uurTekst : `${uurTekst} en ${minTekst}`;
}
